import {
  PropertyMappingEntityEnum,
  PropertyMappingTypeEnum,
} from '../../../shared/models';
import {
  caseDefaultPropertiesDefinition,
  companyDefaultPropertiesDefinition,
  Property,
  PropertyTypeEnum,
} from '../../../shared/properties';

const LABEL_OVERRIDE: Record<string, string> = {
  'case.risk.level': 'Risk',
  'case.template.id': 'Template',
};

/**
 * List of case properties to include in the assignment rule logic builder
 */
export const CASE_PROPERTIES = [
  'name',
  'externalId',
  'risk.level',
  'template.id',
  'tagIds',
] as const;

/**
 * List of company properties to include in the assignment rule logic builder
 */
export const COMPANY_PROPERTIES = [
  'name',
  'commercialName',
  'registrationNumber',
  'registrationDate',
  'country',
  'status',
  'legalForm',
  'entityLegalForm',
  'bankingInformation',
  'address.city',
  'address.country',
  'address.postalCode',
  'address.region',
  'address.state',
  'address.streetAddress',
  'address.streetAddress2',
  'taxIdentificationNumber',
  'employerIdentificationNumber',
  'classifications',
  'shareCapital',
  'websiteUrl',
] as const;

export type AssignmentRuleProperties = {
  case: Property<PropertyTypeEnum>[];
  company: Property<PropertyTypeEnum>[];
};

/**
 * Get the assignment rule properties including custom properties
 */
export const getAssignmentRuleProperties = (
  customProperties: Property<PropertyTypeEnum>[],
): AssignmentRuleProperties => {
  const caseProperties = [
    // Default case properties
    ...CASE_PROPERTIES.map((propertyKey) => {
      const property = {
        ...caseDefaultPropertiesDefinition[propertyKey],
        mapping: {
          entity: PropertyMappingEntityEnum.case,
          type: PropertyMappingTypeEnum.default,
          key: propertyKey,
        },
      };

      // Special label override for some nested fields
      if (LABEL_OVERRIDE[`case.${propertyKey}`]) {
        property.label = LABEL_OVERRIDE[`case.${propertyKey}`];
      }

      return property;
    }),
    // Custom case properties
    ...customProperties.filter(
      (property) => property.mapping.entity === PropertyMappingEntityEnum.case,
    ),
  ];

  const companyProperties = [
    // Default company properties
    ...COMPANY_PROPERTIES.map((propertyKey) => ({
      ...companyDefaultPropertiesDefinition[propertyKey],
      mapping: {
        entity: PropertyMappingEntityEnum.company,
        type: PropertyMappingTypeEnum.default,
        key: propertyKey,
      },
    })),
    // Custom company properties
    ...customProperties.filter(
      (property) =>
        property.mapping.entity === PropertyMappingEntityEnum.company,
    ),
  ];

  return {
    case: caseProperties,
    company: companyProperties,
  };
};
